import type { Chart, ChartNote, Lane } from '@/types';
import { FOODS } from '@/data/foods';

/**
 * Deterministically generates a beat-aligned chart for a given bpm/duration. This is
 * how the two bundled demo songs get charts that are guaranteed to line up with their
 * (also procedurally generated) audio -- but the output is just plain ChartNote[]
 * JSON-shaped data, identical to what a hand-authored chart file looks like, so
 * charts can freely be replaced with manually authored JSON later without any of the
 * playback code changing.
 *
 * The phrase patterns deliberately cover the spatial gameplay variety called for by
 * the design: cats closing in on each other, cats moving apart, one cat holding
 * still while the other sweeps, mirrored motion, simple alternation, and
 * simultaneous same-position catches.
 */

type PatternName = 'alternate' | 'mirror' | 'together' | 'apart' | 'stationaryRightMoves' | 'simultaneousSame';

const PATTERN_ORDER: PatternName[] = ['alternate', 'together', 'mirror', 'apart', 'stationaryRightMoves', 'simultaneousSame'];

// C5, D5, E5, G5, A5, C6, A5, G5 as MIDI note numbers.
const PITCH_SCALE: number[] = [72, 74, 76, 79, 81, 84, 81, 79];

export interface ChartGeneratorOptions {
  bpm: number;
  durationSec: number;
  leadInBeats?: number;
  tailBeats?: number;
  /** Beats between notes; lower = denser/harder. */
  stepBeats?: number;
  noteTravelTime?: number;
}

function beatToNote(
  beatGlobalIndex: number,
  time: number,
  lane: Lane,
  x: number
): ChartNote {
  const food = FOODS[beatGlobalIndex % FOODS.length];
  const pitch = PITCH_SCALE[beatGlobalIndex % PITCH_SCALE.length];
  return { time, lane, x: clamp01(x), type: food.id, pitch };
}

function clamp01(v: number): number {
  return Math.max(0.08, Math.min(0.92, v));
}

export function generateChart(options: ChartGeneratorOptions): Chart {
  const { bpm, durationSec, leadInBeats = 8, tailBeats = 8, stepBeats = 1 } = options;
  const secPerBeat = 60 / bpm;
  const startBeat = leadInBeats;
  const endBeat = Math.floor(durationSec / secPerBeat) - tailBeats;

  const notes: ChartNote[] = [];
  let globalIndex = 0;
  let phraseIndex = 0;

  for (let beat = startBeat; beat < endBeat; beat += stepBeats) {
    const beatInPhrase = Math.floor((beat - startBeat) / stepBeats) % 8;
    if (beatInPhrase === 0) phraseIndex++;
    const pattern = PATTERN_ORDER[phraseIndex % PATTERN_ORDER.length];
    const t = beatInPhrase / 7; // 0..1 across the 8-beat phrase
    const time = beat * secPerBeat;

    switch (pattern) {
      case 'alternate': {
        const lane: Lane = beatInPhrase % 2 === 0 ? 'left' : 'right';
        notes.push(beatToNote(globalIndex++, time, lane, 0.5));
        break;
      }
      case 'mirror': {
        notes.push(beatToNote(globalIndex++, time, 'left', 0.2 + t * 0.6));
        notes.push(beatToNote(globalIndex++, time, 'right', 0.8 - t * 0.6));
        break;
      }
      case 'together': {
        notes.push(beatToNote(globalIndex++, time, 'left', 0.85 - t * 0.35));
        notes.push(beatToNote(globalIndex++, time, 'right', 0.15 + t * 0.35));
        break;
      }
      case 'apart': {
        notes.push(beatToNote(globalIndex++, time, 'left', 0.5 - t * 0.35));
        notes.push(beatToNote(globalIndex++, time, 'right', 0.5 + t * 0.35));
        break;
      }
      case 'stationaryRightMoves': {
        if (beatInPhrase % 2 === 0) notes.push(beatToNote(globalIndex++, time, 'left', 0.5));
        notes.push(beatToNote(globalIndex++, time, 'right', 0.2 + ((beatInPhrase * 0.13) % 0.6)));
        break;
      }
      case 'simultaneousSame': {
        const x = 0.3 + 0.4 * ((Math.sin(beatInPhrase * 1.3) + 1) / 2);
        notes.push(beatToNote(globalIndex++, time, 'left', x));
        notes.push(beatToNote(globalIndex++, time, 'right', x));
        break;
      }
    }
  }

  return { noteTravelTime: options.noteTravelTime, notes };
}
